import { Fragment, useEffect, useRef } from "react";
import { useDuckSidebarResizeContext } from "duck/ui/chat/contexts/useDuckSidebarResizeContext";
import { DUCK_SIDEBAR_MAX_WIDTH } from "duck/ui/constants";
import { useTranslation } from "react-i18next";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";

import { formatCurrencyValue } from "shared/locale/format";

import LaborRecommendationSection from "./LaborRecommendationSection";
import OtherRecommendationSection from "./OtherRecommendationSection";
import PartRecommendationSection from "./PartRecommendationSection";
import TableDivider from "./TableDivider";
import { PriceAdvice, ServiceCenter } from "./types";
import {
  buildAssetFilters,
  hasBothOtherAndLaborOrParts,
  hasLabor,
  hasParts,
  hasPriceAnalysis,
} from "./utils";

type PriceAdvisorMessagePropsAsString = {
  content: string;
  parsedObject?: never;
  appendMessageWithPydanticAi: boolean;
  isStreaming: boolean;
};

type PriceAdvisorMessagePropsAsParsedObject = {
  content?: never;
  parsedObject: PriceAdvice;
  appendMessageWithPydanticAi: boolean;
  isStreaming: boolean;
};

type PriceAdvisorMessageProps =
  PriceAdvisorMessagePropsAsString | PriceAdvisorMessagePropsAsParsedObject;

const PriceAdvisorMessage = (props: PriceAdvisorMessageProps) => {
  const { t } = useTranslation("duck");
  const { sidebarWidth, setSidebarWidth } = useDuckSidebarResizeContext();
  const hasAdjustedSidebarRef = useRef(false);

  useEffect(() => {
    if (hasAdjustedSidebarRef.current) return;

    if (sidebarWidth >= DUCK_SIDEBAR_MAX_WIDTH) {
      hasAdjustedSidebarRef.current = true;

      return;
    }

    hasAdjustedSidebarRef.current = true;
    setSidebarWidth(DUCK_SIDEBAR_MAX_WIDTH);
  }, [sidebarWidth, setSidebarWidth]);

  const { parsedObject, isStreaming } = props;

  let priceAdvice: PriceAdvice;
  if (parsedObject) {
    priceAdvice = parsedObject;
  } else {
    let parsedMessage;

    try {
      parsedMessage = JSON.parse(props.content);
    } catch {
      return null;
    }

    if (!parsedMessage?.content) {
      return null;
    }

    priceAdvice = parsedMessage.content;
  }

  const recommendations = Array.isArray(priceAdvice?.repair_recommendations)
    ? priceAdvice.repair_recommendations
    : [];
  const serviceCenters = Array.isArray(priceAdvice?.service_centers)
    ? priceAdvice.service_centers
    : [];
  const hasAssetData = Boolean(priceAdvice?.asset_data);

  const isIncompleteData =
    !Array.isArray(priceAdvice?.repair_recommendations) ||
    !Array.isArray(priceAdvice?.service_centers);

  if (isIncompleteData && !isStreaming) {
    return <div>{t("price_advisor_error")}</div>;
  }

  const normalizedRecommendations = recommendations.map((recommendation) => ({
    ...recommendation,
    part_recommendations: Array.isArray(recommendation.part_recommendations)
      ? recommendation.part_recommendations.map((partRecommendation) => ({
          ...partRecommendation,
          service_center_alternatives: Array.isArray(
            partRecommendation.service_center_alternatives
          )
            ? partRecommendation.service_center_alternatives
            : [],
          web_alternatives: Array.isArray(partRecommendation.web_alternatives)
            ? partRecommendation.web_alternatives
            : [],
        }))
      : [],
    labor_recommendations: Array.isArray(recommendation.labor_recommendations)
      ? recommendation.labor_recommendations.map((laborRecommendation) => ({
          ...laborRecommendation,
          service_center_alternatives: Array.isArray(
            laborRecommendation.service_center_alternatives
          )
            ? laborRecommendation.service_center_alternatives
            : [],
        }))
      : [],
    other_recommendation: recommendation.other_recommendation ?? null,
  }));

  const assetFilters = hasAssetData
    ? buildAssetFilters(priceAdvice.asset_data)
    : "";

  const serviceCentersByID = serviceCenters.reduce(
    (acc, serviceCenter) => ({
      ...acc,
      [serviceCenter.id]: serviceCenter,
    }),
    {} as Record<string, ServiceCenter>
  );

  return (
    // The chat's wrap-anywhere would shrink the absolutely positioned price labels to one character per line.
    <div className="min-w-2xl wrap-normal">
      <div className="border-b border-t border-gray-500 bg-gray-50 text-gray-600 font-bold text-lg">
        {t("price_check")}
      </div>
      {normalizedRecommendations.length === 0 && !isStreaming && (
        <div>{t("no_price_recommendations")}</div>
      )}
      {normalizedRecommendations.map((recommendation, recommendationIndex) => (
        <Fragment
          key={`${
            recommendation.repair_id ||
            recommendation.description ||
            recommendation.service_performed ||
            "recommendation"
          }-${recommendationIndex}`}
        >
          <Table
            size="small"
            sx={{
              "& .MuiTableCell-root": {
                borderBottom: "none",
              },
              mb: 1,
              tableLayout: "fixed",
            }}
          >
            <TableBody>
              <TableRow>
                <TableCell sx={{ pl: 0 }}>
                  <div className="font-bold">
                    {recommendation.description ||
                      recommendation.service_performed}
                  </div>
                </TableCell>
                <TableCell>
                  {t("current_vendor_quote")}{" "}
                  <span className="font-bold">
                    {formatCurrencyValue(
                      recommendation.currency_code,
                      recommendation.cost_total
                    )}
                  </span>
                </TableCell>
              </TableRow>
              {!hasPriceAnalysis(recommendation) && (
                <TableRow>
                  <TableCell colSpan={2}>
                    <div className="text-xs ml-4">{t("no_price_analysis")}</div>
                  </TableCell>
                </TableRow>
              )}
              {hasParts(recommendation) && (
                <TableRow className="bg-slate-200">
                  <TableCell sx={{ width: "40%" }}>
                    <span className="font-semibold">{t("part_vendor")}</span>
                  </TableCell>
                  <TableCell sx={{ width: "60%" }}>
                    <span className="font-semibold">
                      {t("price_range_median_quote")}
                    </span>
                  </TableCell>
                </TableRow>
              )}
              {recommendation.part_recommendations.map(
                (part_recommendation, partIndex) => (
                  <PartRecommendationSection
                    key={`${
                      part_recommendation.vmrs_id ||
                      recommendation.repair_id ||
                      "part"
                    }-${partIndex}`}
                    partRecommendation={part_recommendation}
                    recommendation={recommendation}
                    serviceCentersByID={serviceCentersByID}
                    assetFilters={assetFilters}
                    serviceCenterCurrent={priceAdvice.service_center_current}
                    workOrderID={priceAdvice.work_order_id}
                    workOrderDate={priceAdvice.work_order_date}
                    consideredHistoryDays={priceAdvice.considered_history_days}
                    isStreaming={isStreaming}
                    hasAssetData={hasAssetData}
                  />
                )
              )}
              {hasLabor(recommendation) && (
                <>
                  {hasParts(recommendation) && (
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{ borderBottom: "none", p: 0, height: 12 }}
                      />
                    </TableRow>
                  )}
                  <TableRow className="bg-slate-200 mt-2">
                    <TableCell sx={{ width: "40%" }}>
                      <span className="font-semibold">{t("labor_vendor")}</span>
                    </TableCell>
                    <TableCell sx={{ width: "60%" }}>
                      <span className="font-semibold">
                        {t("price_range_median_quote")}
                      </span>
                    </TableCell>
                  </TableRow>
                </>
              )}
              {recommendation.labor_recommendations.map(
                (labor_recommendation, laborIndex) => (
                  <LaborRecommendationSection
                    key={`${
                      labor_recommendation.vmrs_id ||
                      recommendation.repair_id ||
                      "labor"
                    }-${laborIndex}`}
                    laborRecommendation={labor_recommendation}
                    recommendation={recommendation}
                    serviceCentersByID={serviceCentersByID}
                    assetFilters={assetFilters}
                    serviceCenterCurrent={priceAdvice.service_center_current}
                    workOrderID={priceAdvice.work_order_id}
                    workOrderDate={priceAdvice.work_order_date}
                    consideredHistoryDays={priceAdvice.considered_history_days}
                    isStreaming={isStreaming}
                    hasAssetData={hasAssetData}
                  />
                )
              )}
              {hasBothOtherAndLaborOrParts(recommendation) && (
                <TableDivider withSidePadding />
              )}
              {recommendation.other_recommendation && (
                <OtherRecommendationSection
                  otherRecommendation={recommendation.other_recommendation}
                  recommendation={recommendation}
                  serviceCentersByID={serviceCentersByID}
                  serviceCenterCurrent={priceAdvice.service_center_current}
                  workOrderID={priceAdvice.work_order_id}
                  workOrderDate={priceAdvice.work_order_date}
                  consideredHistoryDays={priceAdvice.considered_history_days}
                  partVMRSIDs={
                    recommendation.other_recommendation.part_vmrs_ids ??
                    recommendation.part_recommendations.map(
                      (partRecommendation) => partRecommendation.vmrs_id
                    )
                  }
                  laborVMRSIDs={
                    recommendation.other_recommendation.labor_vmrs_ids ??
                    recommendation.labor_recommendations.map(
                      (laborRecommendation) => laborRecommendation.vmrs_id
                    )
                  }
                  isStreaming={isStreaming}
                  assetFilters={assetFilters}
                  hasAssetData={hasAssetData}
                />
              )}
              <TableDivider />
            </TableBody>
          </Table>
        </Fragment>
      ))}
    </div>
  );
};

export default PriceAdvisorMessage;
